// ═══════════════════════════════════════════════════════════════
// Debris Field Portal — Mobile-first for drivers, haulers, monitors
// Route: /debrisportal
// Tabs: Overview (all zones) | Ticket (Driver) | Monitor | Assessment
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect, useMemo } from 'react';
import { useAuthStore } from '../../../store/authStore';
import { useDebrisStore } from '../../../store/debrisStore';
import { useDebrisOpsStore } from '../../../store/debrisOpsStore';
import { useIncidentStore } from '../../../store/incidentStore';
import type { DebrisZone } from '../../../types/debris';
import {
  IconLayoutDashboard, IconTicket, IconEye, IconClipboardList,
  IconChevronDown, IconLogout, IconX, IconChevronRight,
} from '@tabler/icons-react';
import '../../shelter/portal/ShelterPortal.css';
import DebrisPortalDashboard  from './DebrisPortalDashboard';
import DebrisPortalTicket     from './DebrisPortalTicket';
import DebrisPortalMonitor    from './DebrisPortalMonitor';
import DebrisPortalAssessment from './DebrisPortalAssessment';

type Tab = 'dashboard' | 'ticket' | 'monitor' | 'assessment';
export type PortalRole = 'driver' | 'road_monitor' | 'tower_monitor' | 'assessor';

const TABS: { id: Tab; label: string; Icon: (p: { size?: number }) => JSX.Element | null }[] = [
  { id: 'dashboard',  label: 'Overview',   Icon: IconLayoutDashboard },
  { id: 'ticket',     label: 'Ticket',     Icon: IconTicket           },
  { id: 'monitor',    label: 'Monitor',    Icon: IconEye              },
  { id: 'assessment', label: 'Assess',     Icon: IconClipboardList    },
];

const ROLE_LABELS: Record<PortalRole, string> = {
  driver:        '🚛 Driver',
  road_monitor:  '🦺 Road Mon.',
  tower_monitor: '🏗️ Tower Mon.',
  assessor:      '📋 Assessor',
};

// Default tab per role
const ROLE_DEFAULT_TAB: Record<PortalRole, Tab> = {
  driver:        'ticket',
  road_monitor:  'monitor',
  tower_monitor: 'monitor',
  assessor:      'assessment',
};

// Tabs that require a zone to be selected
// Drivers bypass zone-gating for the ticket tab — they work across zones, zone is planner metadata
const MONITOR_ZONE_TABS: Tab[] = ['ticket', 'monitor', 'assessment'];
const DRIVER_ZONE_TABS:  Tab[] = ['monitor', 'assessment'];

const ZONE_KEY = 'dp-selected-zone';
const ROLE_KEY = 'dp-role';

// ── Login screen ─────────────────────────────────────────────

function PortalLogin() {
  const { login } = useAuthStore();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) { setError('Email and password are required.'); return; }
    setLoading(true);
    setError('');
    try {
      await login(email.trim(), password);
    } catch (err: any) {
      setError(err?.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="sp-login-wrap">
      <div className="sp-login-card">
        <div className="sp-login-logo-row">
          <img src="/cds/assets/logo/OpenGov-Logo-Blurple.svg" alt="OpenGov" className="sp-login-og-logo" />
          <div className="sp-login-divider" />
          <span className="sp-login-product-name">Emergency Management</span>
        </div>
        <div className="sp-login-title">Debris Field Portal</div>
        <div className="sp-login-sub">Driver · Hauler · Monitor — sign in to continue</div>
        <form onSubmit={submit}>
          {error && <div className="sp-error">{error}</div>}
          <div className="sp-field">
            <label className="sp-label">Email</label>
            <input className="sp-input" type="email" value={email} onChange={e => setEmail(e.target.value)}
              placeholder="you@example.com" autoComplete="email" inputMode="email" />
          </div>
          <div className="sp-field">
            <label className="sp-label">Password</label>
            <input className="sp-input" type="password" value={password} onChange={e => setPassword(e.target.value)}
              placeholder="••••••••" autoComplete="current-password" />
          </div>
          <button type="submit" className="sp-btn sp-btn-primary" disabled={loading}>
            {loading ? 'Signing in…' : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ── Incident picker ──────────────────────────────────────────
// Used both as a full-page gate (no activeIncident) and as an
// overlay drawer (user taps the incident chip to switch).
// Props allow it to be embedded without nuking activeIncident.

function IncidentPicker({
  onSelect,
  onClose,
  fullPage = true,
}: {
  onSelect: (i: import('../../../auth/auth-store').Incident) => void;
  onClose?: () => void;
  fullPage?: boolean;
}) {
  const { incidents, loadIncidents } = useIncidentStore();
  const { logout } = useAuthStore();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Use incidentStore directly — avoids auth-store's auto-select side-effect
    loadIncidents().finally(() => setLoading(false));
  }, [loadIncidents]);

  const active = incidents.filter(i => i.status === 'active' || i.status === 'open');
  const others  = incidents.filter(i => i.status !== 'active' && i.status !== 'open');

  const list = (
    <>
      {loading && (
        <div style={{ textAlign: 'center', color: '#939598', padding: '24px 0', fontSize: 14 }}>
          Loading incidents…
        </div>
      )}

      {!loading && active.map(i => (
        <div key={i.id} className="sp-picker-item" onClick={() => onSelect(i)}
          style={{ cursor: 'pointer', marginBottom: 8, display: 'flex', alignItems: 'center' }}>
          <div style={{ flex: 1 }}>
            <div className="sp-picker-name">{i.name}</div>
            <div className="sp-picker-meta">
              {i.type || i.incidentType || 'Incident'}
              {i.location ? ` · ${i.location}` : ''}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span className="sp-badge sp-badge-green">Active</span>
            <IconChevronRight size={14} style={{ color: '#939598' }} />
          </div>
        </div>
      ))}

      {!loading && others.length > 0 && (
        <div style={{ fontSize: 12, color: '#939598', fontWeight: 600, letterSpacing: '0.5px',
          textTransform: 'uppercase', marginTop: 8, marginBottom: 8 }}>
          Other Incidents
        </div>
      )}
      {!loading && others.map(i => (
        <div key={i.id} className="sp-picker-item" onClick={() => onSelect(i)}
          style={{ cursor: 'pointer', marginBottom: 8, display: 'flex', alignItems: 'center' }}>
          <div style={{ flex: 1 }}>
            <div className="sp-picker-name">{i.name}</div>
            <div className="sp-picker-meta">{i.type || i.incidentType || 'Incident'}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span className="sp-badge sp-badge-gray">{i.status || 'unknown'}</span>
            <IconChevronRight size={14} style={{ color: '#939598' }} />
          </div>
        </div>
      ))}

      {!loading && incidents.length === 0 && (
        <div style={{ textAlign: 'center', color: '#939598', padding: '24px 0', fontSize: 14 }}>
          No incidents found.
        </div>
      )}
    </>
  );

  // Drawer mode (switching incident while already in portal)
  if (!fullPage) {
    return (
      <div className="sp-drawer-overlay" onClick={onClose}>
        <div className="sp-drawer" onClick={e => e.stopPropagation()}>
          <div className="sp-drawer-handle" />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div className="sp-drawer-title" style={{ marginBottom: 0 }}>Switch Incident</div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#7B7D7F' }}>
              <IconX size={20} />
            </button>
          </div>
          {list}
        </div>
      </div>
    );
  }

  // Full-page mode (no incident set yet)
  return (
    <div className="sp-login-wrap">
      <div className="sp-login-card">
        <div className="sp-login-logo-row">
          <img src="/cds/assets/logo/OpenGov-Logo-Blurple.svg" alt="OpenGov" className="sp-login-og-logo" />
          <div className="sp-login-divider" />
          <span className="sp-login-product-name">Emergency Management</span>
        </div>
        <div className="sp-login-title">Select Incident</div>
        <div className="sp-login-sub">Choose the incident you are working in</div>
        {list}
        <button onClick={logout} className="sp-btn sp-btn-secondary" style={{ marginTop: 16 }}>
          Sign Out
        </button>
      </div>
    </div>
  );
}

// ── Role picker ──────────────────────────────────────────────

const ROLES: { id: PortalRole; emoji: string; title: string; desc: string; color: string }[] = [
  { id: 'driver',        emoji: '🚛', title: 'Driver / Crew',       desc: 'Create tickets, add pickup stops, show QR at disposal',   color: '#d97706' },
  { id: 'road_monitor',  emoji: '🦺', title: 'Road Monitor',        desc: 'Sign tickets at loading site, estimate load percentage',   color: '#7c3aed' },
  { id: 'tower_monitor', emoji: '🏗️', title: 'Tower Monitor',       desc: 'Scan QR at disposal, verify load, capture signatures',    color: '#2563eb' },
  { id: 'assessor',      emoji: '📋', title: 'Field Assessor',      desc: 'Log debris locations, ROE status, hazards',               color: '#059669' },
];

function RolePicker({ onSelect }: { onSelect: (r: PortalRole) => void }) {
  return (
    <div className="sp-login-wrap">
      <div className="sp-login-card">
        <div className="sp-login-logo-row">
          <img src="/cds/assets/logo/OpenGov-Logo-Blurple.svg" alt="OpenGov" className="sp-login-og-logo" />
          <div className="sp-login-divider" />
          <span className="sp-login-product-name">Emergency Management</span>
        </div>
        <div className="sp-login-title">What's your role today?</div>
        <div className="sp-login-sub">This customizes your view — you can change it anytime</div>
        {ROLES.map(r => (
          <div key={r.id} onClick={() => onSelect(r.id)} style={{
            border: `2px solid ${r.color}22`, borderRadius: 4, padding: '16px 16px',
            marginBottom: 8, cursor: 'pointer', background: `${r.color}08`,
            display: 'flex', alignItems: 'center', gap: 16,
            transition: 'all 0.1s',
          }}
            onMouseDown={e => (e.currentTarget.style.transform = 'scale(0.98)')}
            onMouseUp={e => (e.currentTarget.style.transform = '')}
          >
            <span style={{ fontSize: 32, lineHeight: 1 }}>{r.emoji}</span>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#1B1C1C', marginBottom: 2 }}>{r.title}</div>
              <div style={{ fontSize: 12, color: '#7B7D7F' }}>{r.desc}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Zone picker drawer ────────────────────────────────────────

function ZonePicker({ zones, current, onSelect, onClose }: {
  zones: DebrisZone[];
  current: DebrisZone | null;
  onSelect: (z: DebrisZone) => void;
  onClose: () => void;
}) {
  const [search, setSearch] = useState('');
  const filtered = search
    ? zones.filter(z => (z.zoneName + z.zoneCode).toLowerCase().includes(search.toLowerCase()))
    : zones;

  return (
    <div className="sp-drawer-overlay" onClick={onClose}>
      <div className="sp-drawer" onClick={e => e.stopPropagation()}>
        <div className="sp-drawer-handle" />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div className="sp-drawer-title" style={{ marginBottom: 0 }}>Select Zone</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#7B7D7F' }}>
            <IconX size={20} />
          </button>
        </div>
        <input className="sp-input" placeholder="Search zones…" value={search}
          onChange={e => setSearch(e.target.value)} style={{ marginBottom: 16 }} />
        {filtered.map(z => {
          const estCY = parseFloat(z.estimatedCubicYards) || 0;
          const actCY = parseFloat(z.actualCubicYards) || 0;
          const pct = estCY > 0 ? Math.min(Math.round((actCY / estCY) * 100), 100) : 0;
          return (
            <div key={z.id} className={`sp-picker-item ${current?.id === z.id ? 'selected' : ''}`}
              onClick={() => onSelect(z)}>
              <div style={{ flex: 1 }}>
                <div className="sp-picker-name">{z.zoneName || z.zoneCode}</div>
                <div className="sp-picker-meta">
                  Pass {z.currentPass || '1'} · {z.status}
                  {z.debrisTypes ? ` · ${JSON.parse(z.debrisTypes || '[]').join(', ')}` : ''}
                </div>
                {estCY > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                    <div style={{ flex: 1, height: 4, background: '#e2e8f0', borderRadius: 99 }}>
                      <div style={{ width: `${pct}%`, height: '100%', background: '#d97706', borderRadius: 99 }} />
                    </div>
                    <span style={{ fontSize: 12, color: '#7B7D7F', whiteSpace: 'nowrap' }}>
                      {actCY.toLocaleString()}/{estCY.toLocaleString()} CY
                    </span>
                  </div>
                )}
              </div>
              <span className={`sp-badge ${z.status === 'active' ? 'sp-badge-green' : 'sp-badge-gray'}`}>
                {z.status}
              </span>
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div style={{ textAlign: 'center', color: '#939598', fontSize: 14, padding: '24px 0' }}>No zones found.</div>
        )}
      </div>
    </div>
  );
}

// ── Main portal ───────────────────────────────────────────────

export default function DebrisPortal() {
  const { isAuthenticated, logout, activeIncident, setActiveIncident } = useAuthStore();
  const { zones, trucks, crews, loadZones, loadTrucks, loadCrews } = useDebrisStore();
  const { loadTickets, loadLoadTickets } = useDebrisOpsStore();

  const [role, setRole] = useState<PortalRole | null>(() =>
    (localStorage.getItem(ROLE_KEY) as PortalRole | null)
  );
  const [tab, setTab] = useState<Tab>(() =>
    role ? ROLE_DEFAULT_TAB[role] : 'dashboard'
  );
  const [showPicker, setShowPicker] = useState(false);
  const [showIncidentPicker, setShowIncidentPicker] = useState(false);
  const [showRolePicker, setShowRolePicker] = useState(false);
  const [selectedZoneId, setSelectedZoneId] = useState<number | null>(() => {
    const v = localStorage.getItem(ZONE_KEY);
    return v ? Number(v) : null;
  });

  // Load data when incident is active
  useEffect(() => {
    if (!isAuthenticated || !activeIncident) return;
    loadZones();
    loadTrucks();
    loadCrews();
    loadLoadTickets();
  }, [isAuthenticated, activeIncident, loadZones, loadTrucks, loadCrews, loadLoadTickets]);

  // Auto-select first active zone if none selected
  useEffect(() => {
    if (!selectedZoneId && zones.length > 0) {
      const first = zones.find(z => z.status === 'active') ?? zones[0];
      setSelectedZoneId(first.id);
      localStorage.setItem(ZONE_KEY, String(first.id));
    }
  }, [zones, selectedZoneId]);

  const zone = useMemo(
    () => zones.find(z => z.id === selectedZoneId) ?? null,
    [zones, selectedZoneId],
  );

  const selectZone = (z: DebrisZone) => {
    setSelectedZoneId(z.id);
    localStorage.setItem(ZONE_KEY, String(z.id));
    setShowPicker(false);
  };

  const handleSelectZoneFromDashboard = (z: DebrisZone) => {
    selectZone(z);
    setTab('ticket');
  };

  const selectRole = (r: PortalRole) => {
    setRole(r);
    localStorage.setItem(ROLE_KEY, r);
    setTab(ROLE_DEFAULT_TAB[r]);
    setShowRolePicker(false);
  };

  // Auth gates
  if (!isAuthenticated) return <PortalLogin />;
  if (!activeIncident) return (
    <IncidentPicker
      fullPage
      onSelect={i => setActiveIncident(i)}
    />
  );
  if (!role) return <RolePicker onSelect={selectRole} />;

  // Count in-transit tickets for badge (all zones, current incident)
  const inTransitCount = loadTickets.filter(t => t.status === 'in_transit').length;

  const needsZone = (role === 'driver' ? DRIVER_ZONE_TABS : MONITOR_ZONE_TABS).includes(tab);

  return (
    <div className="sp-shell">
      {/* Top bar */}
      <div className="sp-topbar">
        <img src="/cds/assets/logo/OpenGov-Logo-Blurple.svg" alt="OpenGov" className="sp-topbar-logo" />
        <div className="sp-topbar-divider" />
        <span className="sp-topbar-product">Debris</span>

        {/* Incident chip */}
        <button
          onClick={() => setShowIncidentPicker(true)}
          style={{
            background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 4,
            padding: '3px 8px', fontSize: 12, fontWeight: 600, color: '#1d4ed8',
            cursor: 'pointer', maxWidth: 90, overflow: 'hidden', textOverflow: 'ellipsis',
            whiteSpace: 'nowrap', flexShrink: 0,
          }}
          title="Switch incident"
        >
          {activeIncident.name}
        </button>

        {/* Role chip */}
        <button
          onClick={() => setShowRolePicker(true)}
          style={{
            background: '#F8F8F8', border: '1px solid #e2e8f0', borderRadius: 4,
            padding: '3px 8px', fontSize: 12, fontWeight: 600, color: '#475569',
            cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap',
          }}
          title="Change role"
        >
          {ROLE_LABELS[role]}
        </button>

        {/* Zone selector — only shown when current tab requires it */}
        {needsZone && (
          <button className="sp-shelter-btn" onClick={() => setShowPicker(true)}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {zone ? (zone.zoneName || zone.zoneCode) : 'Select zone…'}
            </span>
            <IconChevronDown size={12} style={{ flexShrink: 0 }} />
          </button>
        )}

        <button onClick={logout} className="sp-topbar-icon-btn" style={{ marginLeft: 'auto' }} title="Sign out">
          <IconLogout size={18} />
        </button>
      </div>

      {/* Role picker drawer */}
      {showRolePicker && (
        <div className="sp-drawer-overlay" onClick={() => setShowRolePicker(false)}>
          <div className="sp-drawer" onClick={e => e.stopPropagation()}>
            <div className="sp-drawer-handle" />
            <div className="sp-drawer-title">Change Role</div>
            {ROLES.map(r => (
              <div key={r.id} onClick={() => selectRole(r.id)} style={{
                border: `2px solid ${role === r.id ? r.color : '#e2e8f0'}`,
                borderRadius: 4, padding: '16px 16px', marginBottom: 8, cursor: 'pointer',
                background: role === r.id ? `${r.color}10` : '#fff',
                display: 'flex', alignItems: 'center', gap: 8,
              }}>
                <span style={{ fontSize: 24 }}>{r.emoji}</span>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#1B1C1C' }}>{r.title}</div>
                  <div style={{ fontSize: 12, color: '#7B7D7F' }}>{r.desc}</div>
                </div>
                {role === r.id && <span className="sp-badge sp-badge-green" style={{ marginLeft: 'auto' }}>Current</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Incident switcher (drawer) */}
      {showIncidentPicker && (
        <IncidentPicker
          fullPage={false}
          onSelect={i => { setActiveIncident(i); setShowIncidentPicker(false); }}
          onClose={() => setShowIncidentPicker(false)}
        />
      )}

      {/* Zone picker */}
      {showPicker && (
        <ZonePicker zones={zones} current={zone} onSelect={selectZone} onClose={() => setShowPicker(false)} />
      )}

      {/* Content */}
      <div className="sp-content">
        {tab === 'dashboard' && (
          <DebrisPortalDashboard onSelectZone={handleSelectZoneFromDashboard} />
        )}

        {tab !== 'dashboard' && needsZone && !zone && (
          <div style={{ padding: 24, textAlign: 'center', color: '#7B7D7F' }}>
            <div style={{ fontSize: 40, marginBottom: 8 }}>📍</div>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>No Zone Selected</div>
            <div style={{ fontSize: 14, marginBottom: 16 }}>
              {zones.length === 0
                ? 'No debris zones found for this incident.'
                : 'Tap the zone selector above to pick a zone.'}
            </div>
            {zones.length > 0 && (
              <button className="sp-btn sp-btn-primary" onClick={() => setShowPicker(true)}>
                Select Zone
              </button>
            )}
          </div>
        )}

        {/* Drivers see all-incident tickets; others see zone-filtered tickets */}
        {tab === 'ticket' && (!needsZone || zone) && <DebrisPortalTicket zone={zone ?? null} role={role} />}
        {tab === 'monitor'    && zone && <DebrisPortalMonitor    zone={zone} role={role} />}
        {tab === 'assessment' && zone && <DebrisPortalAssessment zone={zone} />}
      </div>

      {/* Bottom tab bar */}
      <nav className="sp-bottom-nav">
        {TABS.map(({ id, label, Icon }) => {
          const badge = id === 'monitor' && inTransitCount > 0 ? inTransitCount : 0;
          return (
            <button key={id} className={`sp-nav-btn${tab === id ? ' active' : ''}`} onClick={() => setTab(id)}>
              <div style={{ position: 'relative', display: 'inline-flex' }}>
                <Icon size={20} />
                {badge > 0 && (
                  <span style={{
                    position: 'absolute', top: -5, right: -8,
                    background: '#B12525', color: '#fff', fontSize: 12, fontWeight: 800,
                    padding: '1px 4px', borderRadius: 99, minWidth: 14, textAlign: 'center',
                  }}>
                    {badge}
                  </span>
                )}
              </div>
              {label}
            </button>
          );
        })}
      </nav>
    </div>
  );
}
